import { useInkind } from '@gik/api/inkinds/inkind';
import { usePostsRelated } from '@gik/api/posts/postsRelated';
import type { RelationFilter } from '@gik/api/posts/PostsUrlBuilder';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import bemBlock from '@gik/core/utils/bemBlock';
import { ArticleTile } from '@gik/ui/gik/ArticleTile';
import { FrameBone, ParagraphBone } from '@gik/ui/SkeletonLoader';
import classnames from 'classnames';
import React from 'react';

interface IRelatedArticlesProps extends React.HTMLAttributes<HTMLDivElement> {
  skeletonItems?: number;
  relationFilters?: RelationFilter<string[]>[];
  inkindRouteId: string;
}

const skelBem = bemBlock(`related-articles-rows-skeleton`);
const Skeleton = ({ skeletonItems = relatedArticlesNumItems }: Partial<IRelatedArticlesProps>): React.ReactElement => (
  <div className={`${skelBem()}`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <div key={index} className={skelBem('item')}>
          <FrameBone className={skelBem('image')} />
          <main>
            <ParagraphBone words={20} className={skelBem('text')} />
          </main>
        </div>
      );
    })}
  </div>
);

export function getRelatedArticlesForInkindPageFilters(situationSlugs: string[]): RelationFilter<string[]>[] {
  return [
    {
      taxonomy: 'situation',
      terms: situationSlugs,
    },
    {
      taxonomy: 'article-type',
      terms: ['being-supportive'],
    },
  ];
}
export const relatedArticlesNumItems = 3;

export function RelatedArticles({
  relationFilters,
  inkindRouteId,
  className,
}: IRelatedArticlesProps): React.ReactElement {
  // either inkindRouteId or relationFilters must be defined
  const { data: inkindPage, error: inkindPageError } = useInkind(inkindRouteId);

  const situationSlugs = inkindRouteId && inkindPage?.inkindSituations?.map(s => s.slug);

  // FIXME: we should always pass relationFilters in
  // rather than doing this weird check - RelatedArticles should not rely on an inkind page.
  const _relationFilters: RelationFilter<string[]>[] = inkindRouteId
    ? getRelatedArticlesForInkindPageFilters(situationSlugs)
    : relationFilters;

  const { data: relatedPostsData, error: relatedPostsDataError } = usePostsRelated({
    relationFilters: _relationFilters,
    perPage: relatedArticlesNumItems,
  });
  if (!relatedPostsData) return <Skeleton />;

  const articles = relatedPostsData
    ? relatedPostsData
        .map(post => {
          return WordpressConverter.convertPost(post);
        })
        .filter(WordpressConverter.filterOutPreviewContent)
    : null;

  const bem = bemBlock('related-articles-rows');
  const blockClasses = classnames(bem(), className || '');

  const isLoading = !inkindPage || !relatedPostsData;
  const isError = isLoading && (inkindPageError || relatedPostsDataError);

  if (isLoading) return <Skeleton />;
  if (isError) return <Skeleton />;

  return (
    <div className={blockClasses}>
      {articles?.map(item => {
        if (!item) return null;
        return <ArticleTile article={item} key={item.articleId} layout={'row'} />;
      })}
    </div>
  );
}
